import type {
  AdminOverlayDefinition,
  AdminViewDefinition,
} from "../../components/admin-components.js";
import {
  card,
  drawer,
  modal,
  withDrawer,
  withModal,
} from "../_shared/overlay-definition-helpers.js";

export const jobOverlayViews: AdminViewDefinition[] = [
  withModal("retry-job", "job-detail", retryJobModal()),
  withModal("bulk-replay", "jobs-list", bulkReplayModal()),
  withModal("quarantine-job", "job-detail", quarantineJobModal()),
  withDrawer("dlq-summary", "jobs-list", dlqSummaryDrawer()),
  withDrawer("sanitized-job-payload", "job-detail", sanitizedJobPayloadDrawer()),
];

function retryJobModal(): AdminOverlayDefinition {
  return modal(
    "Retry failed job?",
    [
      card("Job", [
        "The selected Job ID is read from the current route.",
        "Retry is sent to the Admin jobs API.",
        "The original failed job remains immutable.",
      ]),
      card("Reason", [
        "Retry reason is recorded by the backend request boundary.",
        "Only jobs that the backend marks retryable will be accepted.",
      ]),
    ],
    [{ label: "Cancel" }, { label: "Queue new attempt", variant: "primary" }],
  );
}

function bulkReplayModal(): AdminOverlayDefinition {
  return modal(
    "Bulk replay unavailable",
    [
      card("Current status", [
        "Bulk replay does not have a backend API yet.",
        "Use individual job detail retry until bulk replay is implemented.",
      ]),
      card("Required backend contract", [
        "Selected job IDs",
        "Replay eligibility result per job",
        "Operational reason",
        "Audit record and partial-failure report",
      ]),
    ],
    [
      { label: "Cancel" },
      {
        label: "Replay eligible jobs",
        variant: "primary",
        disabled: true,
        disabledReason: "Bulk replay does not have a backend API yet.",
      },
    ],
  );
}

function quarantineJobModal(): AdminOverlayDefinition {
  return modal(
    "Job quarantine unavailable",
    [
      card("Current status", [
        "Job quarantine does not have a backend API yet.",
        "Use the incident runbook when a failed job must not be retried.",
      ]),
      card("Required backend contract", [
        "Selected Job ID",
        "Quarantine reason",
        "External reconciliation state",
        "Audit record and operator identity",
      ]),
    ],
    [
      { label: "Cancel" },
      {
        label: "Quarantine",
        variant: "danger",
        disabled: true,
        disabledReason: "Job quarantine does not have a backend API yet.",
      },
    ],
  );
}

function sanitizedJobPayloadDrawer(): AdminOverlayDefinition {
  return drawer(
    "Sanitized job payload unavailable",
    "A safe payload endpoint is not implemented yet.",
    [
      card("Current status", [
        "The Admin jobs API currently returns safe summary/detail fields only.",
        "This drawer intentionally does not show sample payload JSON.",
      ]),
      card("Required backend contract", [
        "Sanitized payload projection",
        "Redaction proof for secrets, URLs, tokens, and customer contact data",
        "Correlation ID for backend log lookup",
      ]),
    ],
  );
}

function dlqSummaryDrawer(): AdminOverlayDefinition {
  return drawer(
    "Dead-letter queue summary unavailable",
    "A live DLQ summary endpoint is not implemented yet.",
    [
      card("Current status", [
        "The Admin jobs API lists jobs and job details, but not queue-level DLQ aggregates.",
        "This drawer intentionally does not show sample queue counts.",
      ]),
      card("Required backend contract", [
        "Queue identifiers",
        "Oldest-message age",
        "Retryable and non-retryable counts",
        "Reconciliation guidance and audit trail",
      ]),
    ],
  );
}
